import * as d3 from 'd3';
import * as moment from 'moment';
import data from '../../data/data.json';
import _ from 'lodash';
import { swoopyDrag } from './swoopy-drag';
import { wordwrap } from 'd3-jetpack';
import { intcomma, apmonth } from 'journalize';

// a reference to the default graphic container, change if needed
const container = d3.select('#line');

// a helper function to grab the container's width
const getFrameWidth = () => container.node().offsetWidth;

//set margins
let margin = {
  top: 35,
  left: 50,
  right: 50,
  bottom: 105,
};

let breakPoint = 750;

//grabbing totals
let dataTotals = data['All Totals'];

//changing to moment date+time objects
dataTotals.forEach(d => (d.Month = moment(d.Month, 'MMM YYYY')));

//grabbing totals and ranges
let dateRange = d3.extent(dataTotals, d => d.Month),
  totalsMax = d3.max(dataTotals, d => d.Total);

let formatYearAbbreviated = d3.timeFormat("'%y"),
  formatYear = d3.timeFormat('%Y');

function customFormat(d, i) {
  if (i != 0) return formatYearAbbreviated(d);
  else return formatYear(d);
}

//set ranges
let x = d3.scaleTime().domain(dateRange);
let y = d3.scaleLinear().domain([0, totalsMax]);

//append svg
let svg = container.append('svg'),
  g = svg.append('g').attr('class', 'line-chart');

let xAxis = g.append('g').attr('class', 'axis axis--x'),
  yAxis = g.append('g').attr('class', 'axis axis--y');

let path = g
  .append('path')
  .data([dataTotals])
  .attr('class', 'line')
  .attr('fill', 'none')
  .attr('stroke', '#FFC200')
  .attr('stroke-width', 1.5);

let presidencies = g.append('g').attr('class', 'presidencies');

let presidencyGroups = presidencies
  .selectAll('g')
  .data([
    {
      president_desktop: 'George W. Bush',
      president_mobile: 'Bush',
      start: 'January 2001',
      end: 'January 2009',
    },
    {
      president_desktop: 'Barack Obama',
      president_mobile: 'Obama',
      start: 'January 2009',
      end: 'January 2017',
    },
    {
      president_desktop: 'Donald Trump',
      president_mobile: 'Trump',
      start: 'January 2017',
      end: 'January 2021',
    },
  ])
  .enter()
  .append('g');

let presidentText = presidencyGroups.append('text');

let presidentSpans = presidencyGroups
  .append('rect')
  .attr('fill', (d, i) => {
    if (i % 2 == 0) return 'gray';
    else return 'none';
  })
  .style('opacity', 0.1);

let labels = g.append('g').attr('class', 'labeled-elements');

let labelElements = labels
  .selectAll('g')
  .data(getLabeledData(data['All Totals'], ['March 2000']))
  .enter()
  .append('g')
  .attr('class', 'label-element');

labelElements
  .append('circle')
  .attr('fill', '#FFC200')
  .attr('r', 4)
  .attr('class', 'label-point');

let labelText = labelElements.append('g').attr('class', 'label-text');

labelText
  .append('text')
  .text(d => {
    return `${intcomma(d.Total)}`;
  })
  .attr('font-weight', 700);

labelText
  .append('text')
  .text('apprehensions')
  .attr('font-weight', 700)
  .attr('dy', 15);

let apprehensionsMonthLabel = labelText.append('text').text((d, i) => {
  if (i === 0) {
    let month = apmonth(new Date(d.Month)),
      year = d.Month.format('YYYY');

    return `in ${month} ${year}`;
  } else return '';
});

let metaInfo = g
  .append('g')
  .attr('class', 'meta')
  .attr('font-size', '12px')
  .attr('fill', 'hsl(0, 0%, 47%)');

// this is the same function findChartMeta() in nunjucks.js
// find a way to import that function here
metaInfo
  .append('text')
  .text(`Note: ${data.Meta.find(d => d.Chart === 'Line').Note}`)
  .attr('y', 45);

metaInfo
  .append('text')
  .html(
    `Source: <a style="fill: hsl(0, 0%, 47%); text-decoration: underline;"href="https://www.cbp.gov/sites/default/files/assets/documents/2019-Mar/bp-total-monthly-apps-sector-area-fy2018.pdf">${
      data.Meta.find(d => d.Chart === 'Line').Source
    }</a>`
  )
  .attr('y', 65);

// arrow marker, need to append to the svg first to use
svg
  .append('marker')
  .attr('id', 'arrow')
  .attr('viewBox', '-10 -10 20 20')
  .attr('markerWidth', 10)
  .attr('markerHeight', 10)
  .attr('fill', 'hsl(0, 0%, 29%)')
  .attr('orient', 'auto-start-reverse')
  .append('path')
  .attr('d', 'M-6.75,-6.75 L 0,0 L -6.75,6.75');

let swoopySel = svg.append('g').attr('class', 'swoopy-drag-lines');

// annotations for swoopyDrag
let annotations = [
  {
    xVal: moment('2007', 'YYYY'),
    yVal: 110000,
    path: 'M8,-19C5,-45,21,-58,38,-59',
    text: 'Spikes are due to seasonal fluctuations of migrants.',
    textOffset: [44, -86],
  },
  {
    xVal: moment('2014', 'YYYY'),
    yVal: 50000,
    path: 'M15,-29C20,-57,11,-75,-16,-85',
    text: 'An uptick in unaccompanied minors occurred under Obama.',
    textOffset: [-178, -112],
  },
];

let mobileAnnotations = [
  {
    xVal: moment('2007', 'YYYY'),
    yVal: 110000,
    path: 'M4,-13C5,-35,19,-44,39,-44',
    text: 'Spikes are due to seasonal fluctuations of migrants.',
    textOffset: [43, -47],
  },
];

function resize() {
  let frameWidth = getFrameWidth(),
    frameHeight;

  if (frameWidth < breakPoint) {
    frameHeight = 275;
    margin.left = 30;
    margin.right = 10;
  } else {
    frameHeight = 500;
    margin.left = 50;
    margin.right = 115;
  }

  let chartWidth = frameWidth - margin.left - margin.right,
    chartHeight = frameHeight - margin.bottom;

  svg.attr('width', frameWidth).attr('height', frameHeight);
  g.attr('width', chartWidth).attr(
    'transform',
    `translate(${margin.left},${margin.top})`
  );

  x.range([0, chartWidth]);
  y.range([chartHeight, 0]);

  xAxis
    .call(d3.axisBottom(x).tickFormat(customFormat))
    .attr('transform', `translate(0,${frameHeight - margin.bottom})`);

  yAxis.call(
    d3
      .axisLeft(y)
      .ticks(6)
      .tickFormat(d3.format('~s'))
      .tickSize(-chartWidth)
  );

  let line = d3
    .line()
    .x(d => x(d.Month))
    .y(d => y(d.Total));

  path.attr('d', line);

  presidencyGroups.attr(
    'transform',
    d => `translate(${(0, x(moment(d.start, 'MMM YYYY')))})`
  );

  presidentSpans
    .attr('height', chartHeight)
    .attr(
      'width',
      d => x(moment(d.end, 'MMM YYYY')) - x(moment(d.start, 'MMM YYYY'))
    );

  presidentText
    .text(d =>
      frameWidth < breakPoint ? d.president_mobile : d.president_desktop
    )
    .attr('font-size', frameWidth < breakPoint ? '12px' : '14px')
    .attr('dx', frameWidth < breakPoint ? 5 : 10)
    .attr('dy', frameWidth < breakPoint ? 14 : 20);

  labelElements
    .attr('transform', d => `translate(${x(d.Month)},${y(d.Total)})`)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '14px');

  labelText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth < breakPoint) {
        return 'translate(-15,-50)';
      } else return 'translate(10, -21)';
    })
    .attr('text-anchor', (d, i) => {
      if (i === 0 && frameWidth < breakPoint) {
        return 'end';
      } else return 'start';
    });

  apprehensionsMonthLabel.attr('dy', frameWidth < breakPoint ? 33 : 35);

  metaInfo.attr('transform', `translate(0, ${chartHeight})`);

  let swoopy = swoopyDrag()
    .x(function(d) {
      return x(d.xVal);
    })
    .y(function(d) {
      return y(d.yVal);
    })
    .draggable(false)
    .annotations(frameWidth < breakPoint ? mobileAnnotations : annotations);

  swoopySel
    .attr('transform', `translate(${margin.left},${margin.top})`)
    .call(swoopy)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '14px');

  swoopySel.selectAll('text').each(function(d) {
    d3.select(this)
      .text('')
      .tspans(wordwrap(d.text, frameWidth < breakPoint ? 18 : 24));
  });

  swoopySel.selectAll('path').attr('marker-start', 'url(#arrow)');
}

resize();
window.addEventListener('resize', _.debounce(resize, 200));

// dates should be listed in this format: April 2017
function getLabeledData(data, dates) {
  // always annotate the latest datapoint
  let labeledData = [data[data.length - 1]];
  dates.forEach(date => {
    date = moment(date, 'MMM YYYY');
    let foundData = data.find(d => d.Month.isSame(date));
    if (foundData) {
      labeledData.push(foundData);
    }
  });
  return labeledData;
}
